<template>
  <div class="modal fade" :id="`${tag || 'csv'}FormModal`" tabindex="-1" aria-labelledby="csvFormModalTitle">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="csvFormModalTitle">{{ msg }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <input class="form-control form-control-sm" type="file" accept=".csv" id="uploadCsv" @change="parseCsv" />
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button type="button" class="btn btn-primary" @click="saveObj">Upload</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, onMounted } from 'vue';
import { Modal } from 'bootstrap';
import Papa from 'papaparse';
import { camelCase } from 'lodash-es';

const emit = defineEmits(['formDataSubmitted', 'formClosed']);

const props = defineProps({
  tag: String,   // id prefix for FormModal div
  msg: String,   // title/id to use in modal header
  schema: Object,   // schema used to validate/transform import
  types: Object,   // custom types used for database columns/validation
});

const state = reactive({
  modalMounted: null,
  importedCsv: null,
});

onMounted(() => {
  state.modalMounted = new Modal(`#${props.tag || 'csv'}FormModal`, {});
  openModal();
});

function openModal() {
  state.modalMounted.show();
}

function closeModal() {
  state.modalMounted.hide();
  emit('formClosed');
}

async function saveObj() {
  const error = {};
  for (const [index, row] of state.importedCsv.entries()) {
    for (const [k, v] of Object.entries(row)) {
      const col = props.schema.find(field => field.name === camelCase(k));
      if (!col) {
        error.row = index;
        error.reason = `Invalid column ${k} not in schema`;
        break;
      }
    //   console.debug(`Assessing row ${index + 1} ${k} = ${v}: ${JSON.stringify(col)}`);
      if (v === null) {
        if (Object.hasOwn(col, 'default') && col.default != 'current_timestamp')
          row[k] = col.default;
        // else let API handle nulls
      } else if (col.type.includes('varchar') && !col.type.includes('[]')) {
        row[k] = `${v}`;
      } else if (col.type.includes('int') && typeof v !== 'number') {
        try {
          row[k] = parseInt(v);
        } catch (err) {
          error.row = index;
          error.reason = `Invalid integer ${v}`;
          break;
        }
      } else if (col.type.includes('float') && typeof v !== 'number') {
        try {
          row[k] = parseFloat(v);
        } catch (err) {
          error.row = index;
          error.reason = `Invalid float ${v}`;
          break;
        }
      } else if (col.type.includes('_type')) {
        const tDef = props.types.find(x => Object.hasOwn(x, col.type));
        if (tDef && Array.isArray(tDef[col.type])) {
          if (!tDef[col.type].includes(v)) {
            error.row = index;
            error.reason = `Invalid ${col.type} ${v}`;
            break;
          }
        }
      }
    }
    if (Object.hasOwn(error, 'row')) break;
  }
  if (Object.hasOwn(error, 'row')) state.importedCsv = error;
  state.modalMounted.hide();
  emit('formDataSubmitted', state.importedCsv);
}

function parseCsv($event) {
  const file = $event.target.files[0];
  const reader = new FileReader();
  if (file.name.includes('.csv')) {
    reader.onload = (res) => {
      Papa.parse(res.target.result, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: function (parsed) {
          state.importedCsv = parsed.data;
        }.bind(this)
      });
    //   console.debug(`Parsed CSV data: ${JSON.stringify(state.importedCsv)}`);
    };
    reader.onerror = (err) => console.error(err);
    reader.readAsText(file);
  }
}

</script>